import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateRouteRiskEstimate } from "@/lib/lanePredictor";
import {
  estimateTransit,
  formatTransitMoment,
  type DriverType,
  type TransitEstimateResult,
} from "@/lib/transitEstimate";

export interface TransitPredictorArgs {
  loadId: string;
  origin: string;
  destination: string;
  miles: number;
  /** "YYYY-MM-DD" */
  pickupDate: string;
  /** "HH:MM" 24h */
  pickupTime: string;
  driverType: DriverType;
}

export interface TransitPrediction extends TransitEstimateResult {
  /** Delivery after the AI's (bounded) condition adjustment. */
  adjustedDeliveryAt: Date;
  adjustmentHours: number;
  adjustmentReason: string;
  corridorNote: string;
  confidence: number;
  /** false → the AI/conditions lane was unavailable; deterministic math only. */
  conditionsAvailable: boolean;
}

/**
 * Deterministic HOS math runs locally and ALWAYS produces a result.
 * The edge function only annotates it; any failure there degrades to the
 * deterministic estimate with a "conditions data unavailable" note.
 */
export function useTransitPredictor() {
  return useMutation<TransitPrediction, Error, TransitPredictorArgs>({
    mutationFn: async (args) => {
      const [h, m] = args.pickupTime.split(":").map(Number);
      const pickupAt = new Date(`${args.pickupDate}T00:00:00`);
      pickupAt.setHours(h || 0, m || 0, 0, 0);

      const risk = generateRouteRiskEstimate(args.origin, args.destination);

      const base = estimateTransit({
        miles: args.miles,
        pickupAt,
        driverType: args.driverType,
        delayMinutes: risk.routeDelayEstimate,
      });

      const fingerprint = [
        args.origin, args.destination, args.miles,
        args.pickupDate, args.pickupTime, args.driverType,
      ].join("|");

      let note = {
        conditionsAvailable: false,
        corridorNote:
          "Conditions data unavailable — this estimate uses drive time and hours-of-service limits only.",
        confidence: 60,
        adjustmentHours: 0,
        adjustmentReason: "",
      };

      try {
        const { data, error } = await supabase.functions.invoke("atlas-transit-predictor", {
          body: {
            origin: args.origin,
            destination: args.destination,
            miles: args.miles,
            driverType: args.driverType,
            pickupLocal: formatTransitMoment(pickupAt),
            deterministicDeliveryLocal: formatTransitMoment(base.deliveryAt),
            totalHours: base.totalHours,
            drivingHours: base.drivingHours,
            drivingDays: base.drivingDays,
            restBreaks: base.restBreaks,
            delayMinutes: base.delayMinutes,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            cache_load_id: args.loadId,
            cache_key: fingerprint,
            ...risk,
          },
        });
        if (!error && data && typeof data.corridorNote === "string") {
          note = { ...note, ...data };
        }
      } catch (e) {
        console.warn("[useTransitPredictor] conditions lane unavailable:", (e as Error).message);
      }

      const adjustmentHours = Number(note.adjustmentHours) || 0;
      const adjustedDeliveryAt = new Date(
        base.deliveryAt.getTime() + adjustmentHours * 3_600_000,
      );

      return {
        ...base,
        adjustedDeliveryAt,
        adjustmentHours,
        adjustmentReason: note.adjustmentReason || "",
        corridorNote: note.corridorNote,
        confidence: note.confidence,
        conditionsAvailable: !!note.conditionsAvailable,
      };
    },
  });
}
